import type { Pairs } from "boltz-swaps/client";
import { SwapType } from "boltz-swaps/types";
import { VsArrowSmallRight } from "solid-icons/vs";
import { For, Show } from "solid-js";

import { LN, isBitcoinOnlyPair } from "../consts/Assets";
import { useGlobalContext } from "../context/Global";
import ExternalLink from "./ExternalLink";
import { getFeeHighlightClass } from "./Fees";
import LoadingSpinner from "./LoadingSpinner";
import { SwapListAssetIcon } from "./SwapIcons";

type SwapFees = {
    assetSend: string;
    assetReceive: string;
    proFee: number;
    regularFee: number;
    swapType: SwapType;
};

export const FeeComparisonTable = (props: {
    proPairs: Pairs;
    regularPairs: Pairs;
    onSelect: (opportunity: SwapFees) => void;
}) => {
    const { t, bitcoinOnly } = useGlobalContext();

    type AssetPairs = Record<
        string,
        Record<string, { fees: { percentage: number } }>
    >;

    const getSwapFees = (
        proPairs: AssetPairs,
        regularPairs: AssetPairs,
        swapType: SwapType,
    ): SwapFees[] => {
        const result: SwapFees[] = [];

        for (const assetSend of Object.keys(proPairs)) {
            for (const assetReceive of Object.keys(proPairs[assetSend])) {
                result.push({
                    swapType,
                    assetSend: swapType === SwapType.Reverse ? LN : assetSend,
                    assetReceive:
                        swapType === SwapType.Submarine ? LN : assetReceive,
                    proFee: proPairs[assetSend][assetReceive].fees.percentage,
                    regularFee:
                        regularPairs[assetSend][assetReceive].fees.percentage,
                });
            }
        }

        return result;
    };

    const filterOpportunities = () => {
        const pro = props.proPairs;
        const regular = props.regularPairs;

        const swapTypes = [
            { type: SwapType.Chain, key: SwapType.Chain },
            { type: SwapType.Reverse, key: SwapType.Reverse },
            { type: SwapType.Submarine, key: SwapType.Submarine },
        ] as const;

        return swapTypes
            .flatMap(({ type, key }) =>
                getSwapFees(pro[key], regular[key], type).filter(
                    (swap) =>
                        swap.proFee < swap.regularFee &&
                        (!bitcoinOnly() ||
                            isBitcoinOnlyPair(
                                swap.assetSend,
                                swap.assetReceive,
                            )),
                ),
            )
            .sort((a, b) => a.proFee - b.proFee);
    };

    return (
        <Show
            when={props.proPairs && props.regularPairs}
            fallback={<LoadingSpinner />}>
            <table class="fee-comparison-table">
                <thead>
                    <tr>
                        <th>{t("swap")}</th>
                        <th>{t("pro_fee")}</th>
                        <th>{t("regular_fee")}</th>
                    </tr>
                </thead>
                <tbody>
                    <Show
                        when={filterOpportunities().length > 0}
                        fallback={
                            <tr
                                class="fee-comparison-row no-data-row"
                                data-testid="no-opportunities-found">
                                <td colSpan={3}>
                                    <span>
                                        {t("no_opportunities_found.text")}
                                    </span>
                                    <div class="fee-comparison-alerts">
                                        <span>
                                            {t(
                                                "no_opportunities_found.telegram_bot_text_prefix",
                                            )}
                                            <ExternalLink href="https://t.me/boltz_pro_bot">
                                                {t(
                                                    "no_opportunities_found.telegram_bot",
                                                )}
                                            </ExternalLink>
                                            {t(
                                                "no_opportunities_found.telegram_bot_text_middle",
                                            )}
                                            <ExternalLink href="https://smp19.simplex.im/a#6v12YQ1txW3h6YqbCmklTC75qsbFd0WxCEGGrSR2GNw">
                                                {t(
                                                    "no_opportunities_found.simplex",
                                                )}
                                            </ExternalLink>
                                            {t(
                                                "no_opportunities_found.telegram_bot_text_suffix",
                                            )}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        }>
                        <For each={filterOpportunities()}>
                            {(opportunity) => (
                                <tr
                                    class="fee-comparison-row"
                                    onClick={() => props.onSelect(opportunity)}>
                                    <td>
                                        <div class="swaplist-asset">
                                            <SwapListAssetIcon
                                                asset={opportunity.assetSend}
                                            />
                                            <VsArrowSmallRight />
                                            <SwapListAssetIcon
                                                asset={opportunity.assetReceive}
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <span
                                            class={getFeeHighlightClass(
                                                opportunity.proFee,
                                                opportunity.regularFee,
                                            )}>
                                            {opportunity.proFee}%
                                        </span>
                                    </td>
                                    <td>
                                        <strong>
                                            {opportunity.regularFee}%
                                        </strong>
                                    </td>
                                </tr>
                            )}
                        </For>
                    </Show>
                </tbody>
            </table>
        </Show>
    );
};

export default FeeComparisonTable;
